import { cacheAction } from 'vuex-cache';

import apiClient from '@/services/api-client';

/**
 * @template S, R
 * @typedef {import('vuex').ActionTree<S, R>} ActionTree<S, R>
 */
/**
 * @typedef {import('@/store/root.state').RootState} RootState
 * @typedef {import('./employees-list.state').EmployeesListState} EmployeesListState
 */

/**
 * @param {any[]} employees
 * @param {any} offices
 */
const processEmployees = (employees, offices) => {
  for (const employee of employees) {
    const office = offices.find((/** @type {any} */ o) => o.id === employee.office_id);

    employee.office_name = office ? office.name : null;
    
    const employeeWithDivision = /** @type {any} */ (employee);
    
    if (employeeWithDivision.division) {
      employeeWithDivision.division_name = employeeWithDivision.division.name;
      employeeWithDivision.division_id = String(employeeWithDivision.division.id);
    } else {
      employeeWithDivision.division_name = null;
      employeeWithDivision.division_id = null;
    }
  }
};

/** @type {ActionTree<EmployeesListState, RootState>} */
export const employeesListActions = {
  getEmployees: cacheAction(async ({ state, cache, commit, rootGetters, getters }) => {
    await cache.dispatch('shared/offices/getOffices', null, { root: true });
    const offices = rootGetters['shared/offices/offices'];
    const { filters } = state;
    const statusFilter = getters.statusFilter || [];
    
    const filterParams = {
      name_search: filters.name || undefined,
      office_ids: filters.officeIds.length > 0 ? filters.officeIds : undefined,
      division_ids: filters.divisionIds.length > 0 ? filters.divisionIds : undefined,
      system_role_ids: filters.systemRoleIds.length > 0 ? filters.systemRoleIds : undefined,
      status_values: statusFilter.length > 0 ? statusFilter.map((status) => (typeof status === 'object' ? status.id : status)) : undefined,
    };

    const { data } = await apiClient.employeeApi.getEmployeesWithFilters(filterParams);
    const employees = data;

    processEmployees(employees, offices);
    commit('setEmployees', employees);
  }),

  getAllEmployeesForOptions: cacheAction(async ({ cache, commit, rootGetters }) => {
    await cache.dispatch('shared/offices/getOffices', null, { root: true });
    const offices = rootGetters['shared/offices/offices'];
    const { data } = await apiClient.employeeApi.getEmployees();

    const employees = data;
    processEmployees(employees, offices);
    commit('setAllEmployeesForOptions', employees);
  }),

  setNameFilter: ({ commit, dispatch }, name) => {
    commit('setNameFilter', name);
    dispatch('getEmployees');
  },
  setOfficeFilter: ({ commit, dispatch }, officeIds) => {
    commit('setOfficeFilter', officeIds);
    dispatch('getEmployees');
  },
  setDivisionFilter: ({ commit, dispatch }, divisionIds) => {
    commit('setDivisionFilter', divisionIds);
    dispatch('getEmployees');
  },
  setSystemRoleFilter: ({ commit, dispatch }, systemRoleIds) => {
    commit('setSystemRoleFilter', systemRoleIds);
    dispatch('getEmployees');
  },
  setStatusFilter: ({ commit, dispatch }, statuses) => {
    commit('setStatusFilter', statuses);
    dispatch('getEmployees');
  },
};
